import React, { useCallback, useEffect, useState } from "react";
import { formatDate } from "@/lib/formatDate";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Dumbbell, Copy, Eye, EyeOff, RefreshCw, CheckCircle2, XCircle, Clock, AlertTriangle, Send } from "lucide-react";
import { StrongfitOnboardingWizard } from "@/components/StrongfitOnboardingWizard";

const copy = (text, label) => {
  navigator.clipboard.writeText(text);
  toast.success(`${label} kopiert`);
};

function StatusBadge({ status }) {
  const ok = status === "paid" || (status || "").startsWith("http_2");
  const pend = status === "sent" || status === "open";
  const partial = status === "partial";
  const refunded = status === "refunded";
  const Icon = ok ? CheckCircle2 : (pend || partial) ? Clock : XCircle;
  const cls = ok ? "bg-emerald-100 text-emerald-700"
    : partial ? "bg-orange-100 text-orange-700"
    : refunded ? "bg-red-100 text-red-700"
    : pend ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600";
  const label = status === "paid" ? "bezahlt" : status === "sent" ? "offen"
    : status === "partial" ? "Teilzahlung"
    : status === "refunded" ? "erstattet" : status;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${cls}`}>
      <Icon className="h-3 w-3" /> {label}
    </span>
  );
}

const ALERT_LABELS = {
  partial_payment: "Teilzahlung",
  overpayment: "Überzahlung",
  duplicate_payment: "Doppelzahlung",
  cancelled_invoice_payment: "Zahlung auf Storno",
  amount_mismatch: "Betragsabweichung",
  match_suggestion: "Zuordnungs-Vorschlag",
  refund: "Rückerstattung",
  chargeback: "Chargeback",
  sync_failure: "Sync-Fehler",
  payout_mismatch: "Auszahlungs-Abweichung",
};

const PAID_VIA_LABELS = {
  camt053: "Bank",
  manual: "manuell",
  qr_bank_transfer: "Bank (QR)",
  manual_bank_transfer: "Überweisung",
  payrexx_card: "Payrexx Karte",
  payrexx_twint: "TWINT",
  sumup_card: "SumUp",
  cash: "Bar",
  credit: "Guthaben",
  other: "andere",
  payrexx: "Payrexx",
  sumup: "SumUp",
  external_app: "App",
};

function ResyncSection({ requests }) {
  const open = requests.filter((r) => r.status === "open");
  if (!requests.length) return null;
  return (
    <div className="mt-6 border border-slate-200 rounded-xl p-4" data-testid="strongfit-resync-section">
      <div className="flex items-center gap-2 text-sm font-medium text-slate-800 mb-1">
        <Send className="h-4 w-4" /> Nachgeforderte Rechnungen ({open.length} offen)
      </div>
      <p className="text-[11px] text-slate-500 mb-2">
        Rechnungen aus Payrexx/SumUp-Auszahlungen, die in der Partner-App fehlen. Die Partner-App erhält das Webhook-Event
        <code className="mx-1 rounded bg-slate-100 px-1">invoice.resync_requested</code> und muss die Rechnungen erneut per
        <code className="mx-1 rounded bg-slate-100 px-1">POST /api/integration/erp/invoices</code> senden – der Abgleich läuft dann automatisch.
      </p>
      <div className="space-y-1">
        {requests.slice(0, 20).map((r) => (
          <div key={r.id} className="flex items-center gap-3 text-xs" data-testid={`strongfit-resync-${r.invoice_number}`}>
            <span className={`rounded-full px-2 py-0.5 text-[10px] ${r.status === "open" ? "bg-amber-50 text-amber-800 border border-amber-200" : "bg-emerald-50 text-emerald-800 border border-emerald-200"}`}>
              {r.status === "open" ? "offen" : "eingetroffen"}
            </span>
            <span className="font-medium">{r.invoice_number}</span>
            <span className="text-slate-600">CHF {Number(r.amount || 0).toFixed(2)}</span>
            <span className="text-slate-500 truncate max-w-[220px]">{r.customer || ""}</span>
            <span className="text-slate-400">bezahlt {formatDate(r.paid_date)} {r.paid_time ? r.paid_time.slice(0, 5) : ""} · Auszahlung {r.payout_reference}</span>
            <span className="text-slate-400 ml-auto">angefordert {formatDate(r.requested_at)}{r.request_count > 1 ? ` (${r.request_count}×)` : ""}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function IssuesSection({ issues, onMarkPaid, onResolve, onResend, busy }) {
  if (!issues || issues.total === 0) return null;
  return (
    <div className="mt-6 border border-amber-200 bg-amber-50/60 rounded-xl p-4" data-testid="strongfit-issues-section">
      <h3 className="text-sm font-semibold text-amber-800 mb-3 flex items-center gap-2">
        <AlertTriangle className="h-4 w-4" /> Unklare Fälle ({issues.total})
      </h3>
      <div className="space-y-2">
        {issues.alerts.map((a) => (
          <div key={a.alert_id} className="flex items-start justify-between gap-3 text-[13px] bg-white rounded-lg px-3 py-2 border border-amber-100" data-testid={`strongfit-alert-${a.alert_id}`}>
            <div>
              <span className="font-medium text-amber-700">{ALERT_LABELS[a.type] || a.type}</span>
              <span className="text-slate-600"> — {a.message}</span>
            </div>
            <div className="flex gap-1.5 whitespace-nowrap">
              {(a.type === "partial_payment" || a.type === "match_suggestion") && a.invoice_id && (
                <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy}
                  onClick={() => onMarkPaid(a.invoice_id)} data-testid={`strongfit-alert-markpaid-${a.alert_id}`}>
                  {a.type === "match_suggestion" ? "Zuordnung bestätigen" : "Als voll bezahlt markieren"}
                </Button>
              )}
              <Button size="sm" variant="ghost" className="h-7 text-xs" disabled={busy}
                onClick={() => onResolve(a.alert_id)} data-testid={`strongfit-alert-resolve-${a.alert_id}`}>
                Erledigt
              </Button>
            </div>
          </div>
        ))}
        {issues.overdue_invoices.map((inv) => (
          <div key={inv.invoice_id} className="flex items-center justify-between gap-3 text-[13px] bg-white rounded-lg px-3 py-2 border border-amber-100" data-testid={`strongfit-overdue-${inv.invoice_id}`}>
            <div className="text-slate-600">
              <span className="font-medium text-slate-800">{inv.invoice_number}</span>
              {" · "}{inv.customer_name || "—"} · CHF {Number(inv.total || 0).toFixed(2)}
              <span className="text-red-600"> · überfällig seit {formatDate(inv.due_date)}</span>
              {Number(inv.amount_paid) > 0 && (
                <span className="text-orange-600"> · CHF {Number(inv.amount_paid).toFixed(2)} bereits bezahlt</span>
              )}
            </div>
            <Button size="sm" variant="outline" className="h-7 text-xs whitespace-nowrap" disabled={busy}
              onClick={() => onMarkPaid(inv.invoice_id)} data-testid={`strongfit-overdue-markpaid-${inv.invoice_id}`}>
              Manuell als bezahlt markieren
            </Button>
          </div>
        ))}
        {issues.failed_webhooks.map((w) => (
          <div key={w.delivery_id} className="flex items-center justify-between gap-3 text-[13px] bg-white rounded-lg px-3 py-2 border border-amber-100" data-testid={`strongfit-failedwh-${w.delivery_id}`}>
            <div className="text-slate-600">
              <span className="font-medium">Webhook {w.event}</span> · {w.external_id} · {w.attempts} Versuch(e)
              {w.retry_status === "pending"
                ? <span className="text-amber-600"> · nächster Auto-Retry geplant</span>
                : <span className="text-red-600"> · endgültig fehlgeschlagen</span>}
            </div>
            <Button size="sm" variant="outline" className="h-7 text-xs whitespace-nowrap" disabled={busy}
              onClick={() => onResend(w.delivery_id)} data-testid={`strongfit-wh-resend-${w.delivery_id}`}>
              <Send className="h-3 w-3 mr-1" /> Erneut senden
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

export const SettingsStrongfitSection = ({ companyId, isOwner }) => {
  const [settings, setSettings] = useState(null);
  const [activity, setActivity] = useState(null);
  const [issues, setIssues] = useState(null);
  const [resync, setResync] = useState([]);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [busy, setBusy] = useState(false);

  const apiBase = process.env.REACT_APP_BACKEND_URL;

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      const a = await apiClient.get(`/companies/${companyId}/integration/strongfit/activity`);
      setActivity(a.data);
    } catch { /* ignore */ }
    try {
      const i = await apiClient.get(`/companies/${companyId}/integration/strongfit/issues`);
      setIssues(i.data);
    } catch { /* ignore */ }
    try {
      const r = await apiClient.get(`/companies/${companyId}/strongfit/resync-requests`);
      setResync(r.data || []);
    } catch { /* ignore */ }
    if (isOwner) {
      try {
        const s = await apiClient.get(`/companies/${companyId}/integration/strongfit/settings`);
        setSettings(s.data);
        setWebhookUrl(s.data.webhook_url || "");
      } catch { /* ignore */ }
    }
  }, [companyId, isOwner]);

  useEffect(() => { load(); }, [load]);

  const saveWebhook = async () => {
    setBusy(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/integration/strongfit/settings`, { webhook_url: webhookUrl });
      setSettings((p) => ({ ...p, ...r.data }));
      toast.success("Webhook-URL gespeichert");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const rotateKey = async () => {
    if (!window.confirm("Neuen API-Key erzeugen? Der alte Key funktioniert danach NICHT mehr — die angebundene App muss aktualisiert werden.")) return;
    setBusy(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/integration/strongfit/settings`, { rotate_key: true });
      setSettings((p) => ({ ...p, ...r.data }));
      toast.success("Neuer API-Key erzeugt");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const markPaid = async (invoiceId) => {
    if (!window.confirm("Rechnung manuell als bezahlt markieren? Die angebundene App wird per Webhook informiert.")) return;
    setBusy(true);
    try {
      await apiClient.post(`/companies/${companyId}/integration/strongfit/invoices/${invoiceId}/mark-paid`);
      toast.success("Rechnung als bezahlt markiert — Webhook gesendet");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const resolveAlert = async (alertId) => {
    setBusy(true);
    try {
      await apiClient.post(`/companies/${companyId}/integration/strongfit/alerts/${alertId}/resolve`);
      toast.success("Fall als erledigt markiert");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const resendWebhook = async (deliveryId) => {
    setBusy(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/integration/strongfit/webhooks/${deliveryId}/resend`);
      if (r.data.ok) toast.success("Webhook erfolgreich zugestellt");
      else toast.error(`Zustellung fehlgeschlagen (${r.data.status})`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const maskedKey = settings?.api_key
    ? (showKey ? settings.api_key : settings.api_key.slice(0, 8) + "••••••••••••")
    : "—";

  return (
    <Card id="section-app-anbindung" className="p-6" data-testid="settings-strongfit-section">
      <div className="flex items-center gap-3 mb-1">
        <div className="h-10 w-10 rounded-xl bg-orange-600 text-white flex items-center justify-center">
          <Dumbbell className="h-5 w-5" />
        </div>
        <div>
          <h2 className="font-semibold text-slate-900">App-Anbindung (Partner-App)</h2>
          <p className="text-xs text-slate-500">
            Rechnungen, Stunden, Mitarbeiter und Absenzen aus Ihrer App empfangen · Zahlungen per Bankimport abgleichen · «bezahlt»-Signal zurücksenden
          </p>
        </div>
      </div>

      {isOwner && settings && (
        <StrongfitOnboardingWizard companyId={companyId} settings={settings} apiBase={apiBase} />
      )}

      {isOwner && settings && (
        <div className="mt-4 space-y-4">
          <div>
            <div className="text-xs font-medium text-slate-600 mb-1">API-Key (für Ihre App – pro Mandant eindeutig)</div>
            <div className="flex gap-2 items-center flex-wrap">
              <code className="text-xs bg-slate-100 px-3 py-2 rounded-lg font-mono" data-testid="strongfit-api-key">{maskedKey}</code>
              <Button size="sm" variant="outline" onClick={() => setShowKey(!showKey)} data-testid="strongfit-key-toggle">
                {showKey ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </Button>
              <Button size="sm" variant="outline" onClick={() => copy(settings.api_key, "API-Key")} data-testid="strongfit-key-copy">
                <Copy className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="outline" onClick={rotateKey} disabled={busy} data-testid="strongfit-key-rotate">
                <RefreshCw className="h-3.5 w-3.5 mr-1" /> Neu erzeugen
              </Button>
            </div>
          </div>

          <div>
            <div className="text-xs font-medium text-slate-600 mb-1">Base-URL (in Ihrer App hinterlegen)</div>
            <div className="flex gap-2 items-center">
              <code className="text-xs bg-emerald-50 border border-emerald-200 px-3 py-2 rounded-lg font-mono break-all" data-testid="strongfit-base-url">
                {apiBase}
              </code>
              <Button size="sm" variant="outline" onClick={() => copy(apiBase, "Base-URL")} data-testid="strongfit-copy-base-url">
                <Copy className="h-3.5 w-3.5" />
              </Button>
            </div>
            <p className="text-[11px] text-amber-700 mt-1">
              ⚠️ Nur die Domain — OHNE /api/…! Die App ergänzt die Pfade selbst.
            </p>
          </div>

          <div>
            <div className="text-xs font-medium text-slate-600 mb-1">Empfangs-Endpoint (technische Referenz — wird von der App automatisch aufgerufen)</div>
            <div className="flex gap-2 items-center">
              <code className="text-xs bg-slate-100 px-3 py-2 rounded-lg font-mono break-all">
                POST {apiBase}/api/integration/erp/invoices
              </code>
              <Button size="sm" variant="outline" onClick={() => copy(`${apiBase}/api/integration/erp/invoices`, "Endpoint")}>
                <Copy className="h-3.5 w-3.5" />
              </Button>
            </div>
            <p className="text-[11px] text-slate-400 mt-1">
              Header: X-API-Key · JSON: external_id, total, customer_name, qr_reference, due_date · weitere Endpunkte: /worktime (Stunden), /employee, /absence · Verbindungstest: GET {"{Base-URL}"}/api/integration/erp/ping · Alt-Pfad /api/integration/strongfit/… bleibt gültig
            </p>
          </div>

          <div>
            <div className="text-xs font-medium text-slate-600 mb-1">Webhook-URL Ihrer App («Rechnung bezahlt»-Signal)</div>
            <div className="flex gap-2">
              <Input
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                placeholder="https://app.strongfit.ch/api/webhooks/accounting"
                className="text-sm"
                data-testid="strongfit-webhook-input"
              />
              <Button size="sm" onClick={saveWebhook} disabled={busy} data-testid="strongfit-webhook-save">Speichern</Button>
            </div>
            {settings.webhook_secret && (
              <p className="text-[11px] text-slate-400 mt-1 flex items-center gap-1">
                Secret (Header X-Webhook-Secret): <code className="bg-slate-100 px-1 rounded">{settings.webhook_secret.slice(0, 10)}…</code>
                <button className="underline" onClick={() => copy(settings.webhook_secret, "Webhook-Secret")}>kopieren</button>
              </p>
            )}
          </div>
        </div>
      )}
      {!isOwner && (
        <p className="mt-3 text-xs text-amber-600">Nur der Firmen-Inhaber sieht API-Key und Einstellungen.</p>
      )}

      <ResyncSection requests={resync} />

      <IssuesSection
        issues={issues}
        onMarkPaid={markPaid}
        onResolve={resolveAlert}
        onResend={resendWebhook}
        busy={busy}
      />

      <div className="mt-6 grid md:grid-cols-2 gap-4">
        <div>
          <h3 className="text-sm font-semibold text-slate-800 mb-2">
            Empfangene Rechnungen {activity ? `(${activity.open_count} offen)` : ""}
          </h3>
          <div className="space-y-1.5" data-testid="strongfit-invoices-list">
            {(activity?.invoices || []).length === 0 && (
              <p className="text-xs text-slate-400">Noch keine Rechnungen empfangen.</p>
            )}
            {(activity?.invoices || []).map((inv, i) => (
              <div key={i} className="flex items-center justify-between gap-2 text-[13px] border-b border-slate-100 pb-1">
                <span className="truncate">
                  <span className="font-medium">{inv.invoice_number}</span>
                  <span className="text-slate-500"> · {inv.customer_name || "—"}</span>
                </span>
                <span className="flex items-center gap-2 whitespace-nowrap">
                  <span className="font-medium">CHF {Number(inv.total || 0).toFixed(2)}</span>
                  {inv.status === "paid" && inv.paid_via && (
                    <span className="text-[11px] text-slate-400" data-testid={`strongfit-paidvia-${i}`}>
                      via {PAID_VIA_LABELS[inv.paid_via] || inv.paid_via}
                    </span>
                  )}
                  <StatusBadge status={inv.status} />
                </span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-slate-800 mb-2">Gesendete Webhooks</h3>
          <div className="space-y-1.5" data-testid="strongfit-webhooks-list">
            {(activity?.webhooks || []).length === 0 && (
              <p className="text-xs text-slate-400">Noch keine Webhooks gesendet.</p>
            )}
            {(activity?.webhooks || []).map((w, i) => (
              <div key={i} className="flex items-center justify-between gap-2 text-[13px] border-b border-slate-100 pb-1">
                <span className="truncate text-slate-600">
                  {w.event} · {w.external_id} · CHF {Number(w.amount || 0).toFixed(2)}
                  {w.attempts > 1 && <span className="text-slate-400"> · {w.attempts} Versuche</span>}
                </span>
                <span className="flex items-center gap-1.5 whitespace-nowrap">
                  {w.retry_status === "pending" && (
                    <span className="text-[11px] text-amber-600">Auto-Retry läuft</span>
                  )}
                  <StatusBadge status={w.status} />
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
};
